import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X, Send, Link as LinkIcon, ChevronDown, ChevronRight, Copy, Check, Search } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';
import AuditManager from './AuditManager';

const EMPTY = {
  name: '', city: '', province: '', yearsActive: '', specialization: '',
  products: '', productionCapacity: '', complianceLevel: '', certifications: '',
  exportExperience: '', moq: '', price: '', productionDelay: '', modes: '',
  commercialTerms: '', strengths: '', pointsToVerify: '', yuvilleAssessment: '',
  internalNotes: '', verifStatus: {}, website: '',
};

const FIELD_GROUPS = [
  { title: 'Identification', fields: ['name', 'city', 'province', 'website', 'yearsActive', 'specialization'] },
  { title: 'Production', fields: ['products', 'productionCapacity', 'modes', 'moq', 'productionDelay'] },
  { title: 'Commercial', fields: ['price', 'commercialTerms', 'exportExperience'] },
  { title: 'Qualité & conformité', fields: ['complianceLevel', 'certifications'] },
  { title: 'Analyse Yuville', fields: ['strengths', 'pointsToVerify', 'yuvilleAssessment'] },
];

const LABELS = {
  name: 'Nom du manufacturier *', city: 'Ville', province: 'Province', website: 'Site Web',
  yearsActive: "Années d'activité", specialization: 'Spécialisation',
  products: 'Produits fabriqués', productionCapacity: 'Capacité de production',
  modes: 'OEM / ODM / Marque privée', moq: 'MOQ', productionDelay: 'Délais de production',
  price: 'Prix ou fourchette indicative', commercialTerms: 'Conditions commerciales',
  exportExperience: "Expérience à l'exportation",
  complianceLevel: 'Niveau de conformité', certifications: 'Certifications',
  strengths: 'Principaux avantages', pointsToVerify: 'Points à vérifier',
  yuvilleAssessment: 'Appréciation Yuville',
};

const VERIF_LABELS = {
  declared: 'Déclaré',
  document_received: 'Doc. reçu',
  yuville_verified: 'Vérifié Yuville',
  to_verify_audit: 'À vérifier en audit',
};
const VERIF_COLORS = {
  declared: 'bg-gray-100 text-gray-700 border-gray-300',
  document_received: 'bg-blue-50 text-blue-700 border-blue-300',
  yuville_verified: 'bg-green-50 text-green-700 border-green-400',
  to_verify_audit: 'bg-amber-50 text-amber-700 border-amber-400',
};

const VERIFIABLE_FIELDS = ['certifications', 'productionCapacity', 'exportExperience', 'complianceLevel'];

const DECISION_LABELS = {
  '': 'Non décidé',
  to_deepen: 'À approfondir',
  need_more_info: 'Plus d\'info',
  dismiss: 'Écarté',
};
const DECISION_COLORS = {
  '': 'bg-gray-100 text-gray-500',
  to_deepen: 'bg-green-100 text-green-700 border-green-400',
  need_more_info: 'bg-blue-100 text-blue-700 border-blue-400',
  dismiss: 'bg-red-100 text-red-700 border-red-400',
};

const inputCls = 'w-full px-3 py-2 rounded border border-gray-300 focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 outline-none text-sm bg-white transition';

export default function ShortlistManager({ rfqId, projectType, clientEmail, isValidated }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [openAudit, setOpenAudit] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/admin/rfq/${rfqId}/manufacturers`);
      setItems(res.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  const startNew = () => { setForm(EMPTY); setEditing('new'); };
  const startEdit = (m) => {
    setForm({ ...EMPTY, ...m });
    setEditing(m.id);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast({ title: 'Nom du manufacturier obligatoire' }); return; }
    setBusy(true);
    try {
      if (editing === 'new') await api.post(`/admin/rfq/${rfqId}/manufacturers`, form);
      else await api.put(`/admin/manufacturers/${editing}`, form);
      toast({ title: 'Manufacturier enregistré' });
      cancel(); load();
    } catch (err) {
      console.error(err);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Enregistrement impossible', duration: 12000 });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce manufacturier ?')) return;
    await api.delete(`/admin/manufacturers/${id}`);
    load();
  };

  const publish = async () => {
    if (items.length === 0) { toast({ title: 'Ajoutez au moins un manufacturier avant de publier.' }); return; }
    setPublishing(true);
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/publish-shortlist`);
      setPublishedUrl(res.data.url);
      toast({ title: 'Short-list publiée', description: res.data.emailSent ? `E-mail envoyé à ${clientEmail}` : `Lien généré — envoi e-mail désactivé`, duration: 12000 });
    } catch (err) {
      toast({ title: 'Erreur publication', description: err?.response?.data?.detail || 'Impossible', duration: 12000 });
    } finally { setPublishing(false); }
  };

  const copyUrl = () => { navigator.clipboard.writeText(publishedUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const setVerif = (field, val) => setForm((f) => ({ ...f, verifStatus: { ...(f.verifStatus || {}), [field]: val } }));

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h4 className="font-bold text-brand-navy text-sm">Short-list · Manufacturiers présélectionnés ({items.length})</h4>
        <div className="flex gap-2">
          {!editing && (
            <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 transition">
              <Plus className="w-3.5 h-3.5" /> Ajouter
            </button>
          )}
          {items.length > 0 && !editing && (
            <button onClick={publish} disabled={publishing || isValidated} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs font-bold hover:bg-brand-navy/90 transition disabled:opacity-60">
              <Send className="w-3.5 h-3.5" /> {isValidated ? 'Sélection validée' : 'Publier & notifier client'}
            </button>
          )}
        </div>
      </div>

      {publishedUrl && (
        <div className="bg-brand-yellow/10 border border-brand-yellow rounded p-3 mb-4 text-xs">
          <div className="font-bold text-brand-navy mb-1">Lien client généré :</div>
          <div className="flex items-center gap-2">
            <input readOnly value={publishedUrl} className="flex-1 min-w-0 px-2 py-1 bg-white border border-gray-200 rounded text-[11px] font-mono" onFocus={(e) => e.target.select()} />
            <button onClick={copyUrl} className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-200 hover:border-brand-navy text-brand-navy">
              {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copié' : 'Copier'}
            </button>
          </div>
        </div>
      )}

      {editing ? (
        <form onSubmit={save} className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-4">
          {FIELD_GROUPS.map((g) => (
            <details key={g.title} open className="group">
              <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-widest text-brand-yellow mb-2">{g.title}</summary>
              <div className="grid sm:grid-cols-2 gap-3">
                {g.fields.map((f) => (
                  <div key={f} className={f === 'strengths' || f === 'pointsToVerify' || f === 'yuvilleAssessment' || f === 'products' || f === 'certifications' ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-semibold text-brand-navy mb-1">
                      {LABELS[f]}
                      {VERIFIABLE_FIELDS.includes(f) && (
                        <select value={form.verifStatus?.[f] || ''} onChange={(e) => setVerif(f, e.target.value)} className="ml-2 text-[10px] px-1 py-0.5 border border-gray-300 rounded">
                          <option value="">— statut —</option>
                          {Object.entries(VERIF_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      )}
                    </label>
                    {['strengths', 'pointsToVerify', 'yuvilleAssessment', 'certifications'].includes(f) ? (
                      <textarea value={form[f] || ''} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className={inputCls + ' min-h-[60px] resize-y'} />
                    ) : (
                      <input type="text" value={form[f] || ''} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className={inputCls} />
                    )}
                  </div>
                ))}
              </div>
            </details>
          ))}
          <div>
            <label className="block text-xs font-semibold text-red-700 mb-1">🔒 Notes internes (jamais visibles par le client)</label>
            <textarea value={form.internalNotes} onChange={(e) => setForm({ ...form, internalNotes: e.target.value })} className={inputCls + ' min-h-[60px] resize-y bg-red-50'} />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-gray-200">
            <button type="button" onClick={cancel} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
              <X className="w-3.5 h-3.5" /> Annuler
            </button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1 px-4 py-1.5 rounded bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> Enregistrer
            </button>
          </div>
        </form>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Aucun manufacturier ajouté. Cliquez « Ajouter » pour créer la short-list.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="p-3 bg-white border border-gray-200 rounded hover:border-brand-yellow transition">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-brand-navy text-sm">{m.name}</span>
                    {m.city && <span className="text-xs text-gray-500">{m.city}{m.province ? `, ${m.province}` : ''}</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${DECISION_COLORS[m.clientDecision || '']}`}>{DECISION_LABELS[m.clientDecision || '']}</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1 line-clamp-2">{m.specialization} · {m.products}</div>
                </div>
                <div className="shrink-0 flex gap-1">
                  {m.clientDecision === 'to_deepen' && (
                    <button onClick={() => setOpenAudit(openAudit === m.id ? null : m.id)} className={`text-xs px-2 py-1 rounded border font-semibold ${openAudit === m.id ? 'bg-brand-yellow text-brand-navy border-brand-yellow' : 'border-brand-yellow text-brand-navy hover:bg-brand-yellow/10'}`}>
                      <Search className="w-3 h-3 inline" /> Audit 03
                    </button>
                  )}
                  <button onClick={() => startEdit(m)} className="text-xs px-2 py-1 rounded border border-gray-300 text-brand-navy hover:border-brand-yellow">Modifier</button>
                  <button onClick={() => remove(m.id)} className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:border-red-500"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              {openAudit === m.id && <AuditManager manufacturerId={m.id} isValidatedShortlist={isValidated} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
